import { call, put, select, takeLatest } from 'redux-saga/effects';

import { MATERIAL_PROVIDERS, STOCK_TYPES } from 'constants/all';

import {
  materialWidget,
  visualize,
} from 'modules/common/forms/materialWidget/data/actions';
import {
  getPartFilePacket,
  getPartFileUuid,
  getProvider,
} from 'modules/common/forms/materialWidget/data/reducer';
import { getViewerIdForStockResultUuid } from 'modules/common/forms/materialWidget/data/sagas';
import { MATERIAL_INDICATOR } from 'modules/common/viewer/cadViewer/constants';

import { stockRequest as stockRequestService } from 'services/materialService';

import {
  getMaterialById,
  getSupplierMaterialsByMaterialId,
} from 'store/materials/reducer';

import { types, receiveResult, stockRequest } from './actions';

export default function* root() {
  yield takeLatest(types.STOCK_REQUEST_SUBMIT, watchStockRequestSubmit);
}

const constructManualStockResultFromRodOptions = ({
  barEnd,
  buffer,
  cutTolerance,
  cutWidth,
  length,
  manualBarCost,
  manualBarDiameter,
  manualBarLength,
  quantity,
}) => {
  const makes = Math.floor(
    (manualBarLength - barEnd) / (length + buffer + cutWidth + cutTolerance),
  );
  const barCount = Math.ceil(quantity / makes);
  return {
    isManual: true,
    stockType: STOCK_TYPES.CYLINDRICAL,
    quantity,
    sacrificialWidth: cutTolerance + cutWidth,
    length,
    buffer,
    barEnd,
    stockOptions: [
      {
        quantity: barCount,
        makes,
        length: manualBarLength,
        diameter: manualBarDiameter,
        price: manualBarCost,
        label: 'A',
      },
    ],
    totalPrice: manualBarCost * barCount,
  };
};

const getResultKeyFromRodOptions = (
  {
    barEnd,
    buffer,
    cutTolerance,
    cutWidth,
    diameter,
    length,
    manualBarCost, // TODO: have keygen for visualization separate from this so new manual bar cost doesnt force new visualization
    manualBarDiameter,
    manualBarLength,
    materialId,
    quantity,
    submitType,
  },
  uuid,
  bodyId,
) => {
  if (submitType === 'manual') {
    return `${barEnd}${manualBarCost}${manualBarDiameter}${manualBarLength}${buffer}${cutTolerance}${cutWidth}${materialId}${quantity}${diameter}${length}${MATERIAL_INDICATOR}${uuid}${bodyId}`;
  } else {
    return `${barEnd}${buffer}${cutTolerance}${cutWidth}${materialId}${quantity}${diameter}${length}${MATERIAL_INDICATOR}${uuid}${bodyId}`;
  }
};

const doesPacketHaveEnoughInfoForVisualization = (partFilePacket) => {
  const stockInformation = partFilePacket && partFilePacket.stockInformation;
  const cylindrical = stockInformation && stockInformation.cylindrical;
  const hasVisualizeInfo = Boolean((cylindrical || {}).center);
  return Boolean(
    partFilePacket && partFilePacket.hasGeometry && hasVisualizeInfo,
  );
};

function* watchStockRequestSubmit(action) {
  try {
    const { payload } = action;
    const { materialId, submitType } = payload;
    let response;
    const activeUuid = yield select(getPartFileUuid);
    const provider = yield select(getProvider);
    if (submitType === 'query') {
      response = yield call(
        stockRequestService.cylindrical,
        MATERIAL_PROVIDERS[provider].urlParam,
        activeUuid,
        payload,
      );
      // add labels to stock options
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      if (response.stockOptions) {
        response.stockOptions.forEach((option, index) => {
          option.label = alphabet[index];
        });
      }
    } else {
      // This is hacky! - we need to capture the event on the backend even when
      // the user enters manual stock size info. The backend will not provide
      // real results when submitType == 'manual', but will record the event
      yield call(
        stockRequestService.cylindrical,
        MATERIAL_PROVIDERS[provider].urlParam,
        activeUuid,
        payload,
      );
      // These are the real results we will display
      response = constructManualStockResultFromRodOptions(payload);
    }
    const packet = yield select(getPartFilePacket);
    const uuid = packet ? packet.uuid : '';
    const bodyId = yield call(getViewerIdForStockResultUuid, uuid);
    const result = {
      ...response,
      bodyId,
      canVisualize: doesPacketHaveEnoughInfoForVisualization(packet),
      key: getResultKeyFromRodOptions(payload, uuid, bodyId),
      uuid,
      partUuid: packet.partUuid,
    };

    // grab material name
    const supplierMaterials = yield select(
      getSupplierMaterialsByMaterialId,
      materialId,
    );
    if (supplierMaterials && supplierMaterials.length === 1) {
      result.material = supplierMaterials[0].displayName;
    } else {
      const material = yield select(getMaterialById, materialId);
      result.material = (material || {}).name;
    }

    yield put(receiveResult({ result }));

    if (response && response.stockOptions && result.canVisualize) {
      yield put(visualize.showRequest({ result }));
    } else {
      yield put(materialWidget.turnOffVisualizationIfNecessary());
    }

    yield put(stockRequest.success());
  } catch (error) {
    yield put(stockRequest.error(error));
  }
}
